import { apiService } from './core/api.service';

export interface ApiNotification {
    id: string;
    title: string;
    message: string;
    isRead: boolean;
    type: 'info' | 'success' | 'warning' | 'error';
    link?: string;
    createdAt: string;
    module?: 'rh' | 'suppliers' | 'almacen' | null;
}

export type NotificationModule = 'rh' | 'suppliers' | 'almacen';

const SUPPLIER_ADMIN_PATHS = [
    '/super-admin-proveedores',
    '/solicitudes-proveedores',
    '/borradores',
    '/rh/purchase-orders',
    '/pagos',
    '/rh/evaluations',
];

/**
 * Pantallas que no son de ningún módulo en particular.
 *
 * `/dashboard` es la que importa: es donde aterrizas al entrar y la que monta
 * el panel de Almacén, de Proveedores o de RH según tu módulo activo. La ruta
 * es la misma para los tres, así que por sí sola no dice en qué estás.
 */
const RUTAS_SIN_MODULO_PROPIO = ['/dashboard', '/notifications'];

/**
 * En qué módulo está trabajando el usuario, según la pantalla en la que está,
 * o null cuando la ruta no alcanza para saberlo.
 *
 * Se resuelve por la ruta y no por el módulo activo del perfil porque así el
 * cambio es inmediato al navegar y no depende de una petición. Donde la ruta
 * no basta se cae al módulo activo, que es la única fuente que queda.
 */
export const moduloDeLaRuta = (pathname: string): NotificationModule | null => {
    if (pathname.startsWith('/almacen')) return 'almacen';
    if (SUPPLIER_ADMIN_PATHS.some(p => pathname.startsWith(p))) return 'suppliers';
    if (RUTAS_SIN_MODULO_PROPIO.some(p => pathname.startsWith(p))) return null;
    return 'rh';
};

/**
 * En qué módulo está trabajando el usuario. Se conserva con la firma de
 * siempre para quien ya la usaba; en las rutas compartidas responde RH, que es
 * lo que respondía antes.
 */
export const getCurrentAdminModule = (pathname: string): NotificationModule =>
    moduloDeLaRuta(pathname) ?? 'rh';

/**
 * Las notificaciones sin módulo se muestran siempre: son las de antes de que
 * existiera el campo, y esconderlas haría que alguien las perdiera sin aviso.
 *
 * `moduloActivo` es el del perfil, y solo se usa donde la ruta no dice nada.
 * Sin él, alguien de Almacén parado en su propio dashboard veía la campanita
 * filtrada a RH y no se enteraba de una herramienta esperando que la reciba.
 */
export const filterNotificationsByModule = (
    notifications: ApiNotification[],
    pathname: string,
    moduloActivo?: NotificationModule | null
): ApiNotification[] => {
    const currentModule = moduloDeLaRuta(pathname) ?? moduloActivo ?? 'rh';
    return notifications.filter(n => !n.module || n.module === currentModule);
};

const getBaseUrl = (isSupplier: boolean) => isSupplier ? '/suppliers/notifications' : '/notifications';

export const notificationsService = {
    // 1. Obtener todas (CON SILENCIO DE ERRORES)
    getAll: async (isSupplier = false, module?: NotificationModule) => {
        const base = getBaseUrl(isSupplier);
        // El portal del proveedor tiene su propio listado: ahí el módulo no aplica.
        const url = !isSupplier && module ? `${base}?module=${module}` : base;
        return await apiService.request<ApiNotification[]>(url, {
            method: 'GET',
            skipGlobalErrorHandler: true // 👈 Evita logout si falla
        });
    },

    // 2. Obtener conteo (CON SILENCIO DE ERRORES)
    getUnreadCount: async (isSupplier = false) => {
        const url = `${getBaseUrl(isSupplier)}/unread-count`;
        const response = await apiService.request<{ count: number }>(url, {
            method: 'GET',
            skipGlobalErrorHandler: true
        });
        return response.count;
    },

    // 3. Marcar una como leída
    markAsRead: async (id: string, isSupplier = false) => {
        const url = `${getBaseUrl(isSupplier)}/${id}/read`;
        await apiService.request<void>(url, { method: 'PATCH' });
    },

    // 4. Marcar todas
    markAllAsRead: async (isSupplier = false) => {
        const url = `${getBaseUrl(isSupplier)}/read-all`;
        await apiService.request<void>(url, { method: 'PATCH' });
    },

    // 5. Eliminar
    delete: async (ids: string[], isSupplier = false) => {
        const baseUrl = getBaseUrl(isSupplier);
        const deletePromises = ids.map(id =>
            apiService.request<void>(`${baseUrl}/${id}`, {
                method: 'DELETE'
            })
        );
        await Promise.all(deletePromises);
    }
};